import React, { useState, useEffect } from 'react';
import { Banner } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, LCIcon, CLS } from '@/components/common/LCPrimitives';
import { emailTemplateService } from '@/services/apiService';

interface EmailTemplate {
  id: number;
  alert_status: string;
  user_role: string;
  subject_template: string;
  body_template: string;
  created_at: string;
  updated_at: string;
}

const ALERT_STATUSES = ['Amber Alert', 'Red Alert', 'Critical', 'House Call'];
const USER_ROLES = ['admin', 'rm'];

const STATUS_STYLE: Record<string, { color: string; bg: string; border: string }> = {
  'Amber Alert': { color: '#b6770b', bg: 'rgba(255,191,0,0.12)', border: 'rgba(182,119,11,0.30)' },
  'Red Alert':   { color: '#E50025', bg: 'rgba(229,0,37,0.08)',  border: 'rgba(229,0,37,0.30)' },
  'Critical':    { color: '#B80020', bg: 'rgba(184,0,32,0.08)',  border: 'rgba(184,0,32,0.30)' },
  'House Call':  { color: '#B80020', bg: 'rgba(184,0,32,0.08)',  border: 'rgba(184,0,32,0.30)' },
};

const getRoleLabel = (role: string) => role === 'admin' ? 'Admin' : 'Relationship Manager';

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      setTemplates(await emailTemplateService.getAll());
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load email templates');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await emailTemplateService.update(editingTemplate.id, {
        subject_template: editingTemplate.subject_template,
        body_template: editingTemplate.body_template,
      });
      await loadTemplates();
      setEditingTemplate(null);
      setSuccess('Template updated successfully');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update template');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-[#7A7A76] text-[13px]">
        Loading templates…
      </div>
    );
  }

  return (
    <div>
      {error && <Banner variant="error" onClose={() => setError(null)} className="mb-3">{error}</Banner>}
      {success && <Banner variant="success" onClose={() => setSuccess(null)} className="mb-3">{success}</Banner>}

      <Banner variant="info" className="mb-4">
        <strong>Template Variables Available:</strong>{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">portfolio_id</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">client_name</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">previous_status</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">current_status</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">current_cushion_pct</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">aum_line</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">margin_req_line</code>,{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">alert_time</code>.
        Use curly braces:{' '}
        <code className="text-[11px] bg-black/[0.06] px-1">Alert for &#123;portfolio_id&#125;</code>
      </Banner>

      <div className="flex flex-col gap-4">
        {ALERT_STATUSES.flatMap(alertStatus =>
          USER_ROLES.map(role => {
            const template = templates.find(t => t.alert_status === alertStatus && t.user_role === role);
            if (!template) return null;

            const isEditing = editingTemplate?.id === template.id;
            const s = STATUS_STYLE[alertStatus] || { color: '#555', bg: 'transparent', border: '#bbb' };

            return (
              <div key={`${alertStatus}-${role}`} className="border border-black/[0.08] bg-white">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-black/[0.06] bg-[#FAF9F8]">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <LCIcon name="mail" size={14} className="text-[#7A7A76] shrink-0" />
                    <span className="font-semibold text-[13.5px]">
                      {alertStatus} — {getRoleLabel(role)}
                    </span>
                    <span
                      style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}
                      className="inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap"
                    >
                      {alertStatus}
                    </span>
                    <span className="inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap text-[#6464E6] bg-[rgba(100,100,230,0.08)] border border-[rgba(100,100,230,0.25)]">
                      {getRoleLabel(role)}
                    </span>
                  </div>
                  {!isEditing && (
                    <LCBtn variant="outline" size="sm" onClick={() => setEditingTemplate({ ...template })}>
                      Edit
                    </LCBtn>
                  )}
                </div>

                {/* Body */}
                <div className="px-4 py-3.5">
                  {isEditing ? (
                    <div className="flex flex-col gap-3">
                      <div className={CLS.field}>
                        <label className={CLS.fieldLabel}>Subject Template</label>
                        <input
                          className={CLS.input}
                          value={editingTemplate!.subject_template}
                          onChange={e => setEditingTemplate({ ...editingTemplate!, subject_template: e.target.value })}
                        />
                        <div className="text-[11px] text-[#7A7A76] mt-0.5">Subject line for the email</div>
                      </div>
                      <div className={CLS.field}>
                        <label className={CLS.fieldLabel}>Body Template</label>
                        <textarea
                          className={`${CLS.input} min-h-[260px] resize-y font-mono text-[12px]`}
                          value={editingTemplate!.body_template}
                          onChange={e => setEditingTemplate({ ...editingTemplate!, body_template: e.target.value })}
                        />
                        <div className="text-[11px] text-[#7A7A76] mt-0.5">Email body content (use template variables)</div>
                      </div>
                      <div className="flex gap-2">
                        <LCBtn variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving…' : 'Save'}
                        </LCBtn>
                        <LCBtn variant="outline" size="sm" onClick={() => setEditingTemplate(null)} disabled={saving}>
                          Cancel
                        </LCBtn>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2.5">
                      <div>
                        <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-[#7A7A76] mb-1.5">Subject</div>
                        <div className="bg-[#FAF9F8] border border-black/[0.06] px-3 py-2 font-mono text-[12px] text-[#333]">
                          {template.subject_template}
                        </div>
                      </div>
                      <hr className={CLS.hr} />
                      <div>
                        <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-[#7A7A76] mb-1.5">Body</div>
                        <pre className="bg-[#FAF9F8] border border-black/[0.06] px-3 py-2 font-mono text-[12px] text-[#333] whitespace-pre-wrap break-words m-0">
                          {template.body_template}
                        </pre>
                      </div>
                      <div className="text-[11px] text-[#7A7A76]">
                        Last updated: {new Date(template.updated_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }).filter(Boolean)
        )}
      </div>
    </div>
  );
};

export default EmailTemplates;
